import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Drawer, Icon, Input, Button, Tooltip, Breadcrumb, Badge } from 'antd';
import styled from 'styled-components';

import { findSettingsItem } from './settingsItems';

var DrawerLayout = styled.div`
  display: flex;
  min-height: calc(100vh - 120px);
`;

var DrawerSideMenu = styled.div`
  width: 200px;
  min-width: 200px;
  border-right: 1px solid #f0f0f0;
  padding: 0 0 0 0;
  margin: -20px 20px -24px -24px;
  background: #fff;
`;

var SideMenuItem = styled.div`
  padding: 10px 16px;
  font-size: 13px;
  color: ${function(p) { return p.active ? '#1890ff' : '#595959'; }};
  font-weight: ${function(p) { return p.active ? '600' : '400'; }};
  background: ${function(p) { return p.active ? '#e6f7ff' : 'transparent'; }};
  border-left: 3px solid ${function(p) { return p.active ? '#1890ff' : 'transparent'; }};
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${function(p) { return p.active ? '#e6f7ff' : '#fafafa'; }};
    color: #1890ff;
  }
`;

var SideMenuLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 16px 6px;
`;

var DrawerContent = styled.div`
  flex: 1;
  min-width: 0;
`;

var BreadcrumbBar = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  .ant-breadcrumb { font-size: 13px; }
  .ant-breadcrumb a { color: #1890ff; }
  .ant-breadcrumb > span:last-child { color: #8c8c8c; }
`;

var SettingsDrawer = function(props) {
  var location = useLocation();
  var history = useHistory();
  var pathname = location.pathname;
  var isOpen = pathname !== '/configuracoes';

  // Rotas de ação (ex: cadastro-de-tarefas/start) herdam o item da rota base
  var match = findSettingsItem(pathname.replace(/\/start$/, ''));
  var item = match && match.item;
  var child = match && match.child;
  var section = match && match.section;

  var searchState = useState('');
  var searchTerm = searchState[0];
  var setSearchTerm = searchState[1];

  var addTriggerState = useState(0);
  var addTrigger = addTriggerState[0];
  var setAddTrigger = addTriggerState[1];

  React.useEffect(function() {
    setSearchTerm('');
  }, [pathname]);

  React.useEffect(function() {
    if (!isOpen) return;
    var handleKeyDown = function(e) {
      if (e.key === 'Escape') { history.push('/configuracoes'); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return function() { document.removeEventListener('keydown', handleKeyDown); };
  }, [isOpen]);

  var handleClose = function() { history.push('/configuracoes'); };

  var triggerAdd = function() {
    setAddTrigger(function(c) { return c + 1; });
  };

  var iconColor = (section && section.color) || '#1890ff';
  var hasChildren = item && item.children && item.children.length > 0;

  // Build drawer title
  var drawerTitle = React.createElement('div', {
    style: { display: 'flex', alignItems: 'center', gap: 12, width: '100%' }
  },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 } },
      item && React.createElement(Icon, { type: item.icon, style: { fontSize: 18, color: iconColor } }),
      React.createElement('span', { style: { fontWeight: 600, fontSize: 16 } }, item ? item.label : 'Configurações')
    ),
    item && item.hasAdd && (!child || child.hasAdd !== false) && React.createElement('div', { style: { flex: 1, display: 'flex', justifyContent: 'center', gap: 8 } },
      React.createElement(Input, {
        placeholder: 'Pesquisar...',
        prefix: React.createElement(Icon, { type: 'search', style: { color: '#bfbfbf' } }),
        value: searchTerm,
        onChange: function(e) { setSearchTerm(e.target.value); },
        allowClear: true,
        style: { maxWidth: 300 }
      }),
      React.createElement(Tooltip, { title: 'Adicionar novo' },
        React.createElement(Button, { icon: 'plus', shape: 'circle', onClick: triggerAdd })
      )
    ),
    !(item && item.hasAdd && (!child || child.hasAdd !== false)) && React.createElement('div', { style: { flex: 1 } })
  );

  // Breadcrumb - keep it simple
  var breadcrumbEl = React.createElement(BreadcrumbBar, null,
    React.createElement(Breadcrumb, null,
      React.createElement(Breadcrumb.Item, null,
        React.createElement('a', { onClick: function(e) { e.preventDefault(); handleClose(); }, href: '#' }, 'Configurações')
      ),
      section && React.createElement(Breadcrumb.Item, null, section.label)
    )
  );

  var content = props.renderContent ? props.renderContent(pathname, {
    searchTerm: searchTerm,
    addTrigger: addTrigger
  }) : null;

  // Get counts for side menu badges
  var checklistTasks = useSelector(function(state) { return (state.checklists && state.checklists.tasks) || []; });
  var documentTypes = useSelector(function(state) { return (state.documents && state.documents.types) || []; });
  var settings = useSelector(function(state) { return state.settings || {}; });

  var CHECKLIST_STATUS_MAP = { 'entrada': 2, 'entrada-troca': 4, 'saida-encerramento': 1, 'saida-troca': 3 };
  var DOCS_DEPT_MAP = { 'legalizacao': 1, 'administrativo': 2, 'contabil': 3, 'fiscal': 4, 'pessoal': 5, 'sucesso-do-cliente': 6 };
  var VARIABLES_KEY_MAP = {
    'origens': 'leads.acquisition',
    'cadencias-prospeccao': 'prospecting.cadence',
    'cadencias-social-selling': 'socialSelling.cadence',
    'categorias-contas-pagar': 'financeiro.categoria_contas_pagar',
    'categorias-contas-receber': 'financeiro.categoria_contas_receber',
  };

  var getChildCount = function(child) {
    if (!item) return null;
    // Checklist tasks
    if (item.key === 'tarefas-do-checklist') {
      var status = CHECKLIST_STATUS_MAP[child.key];
      if (status) return checklistTasks.filter(function(t) { return t.status === status; }).length;
    }
    // Docs esporádicos
    if (item.key === 'docs-esporadicos') {
      var dept = DOCS_DEPT_MAP[child.key];
      if (dept) return documentTypes.filter(function(t) { return t.department === dept; }).length;
    }
    // System variables
    if (item.key === 'variaveis') {
      var settingsKey = VARIABLES_KEY_MAP[child.key];
      if (settingsKey && settings[settingsKey]) return settings[settingsKey].length;
    }
    return null;
  };

  // Side menu for items with children
  var sideMenuEl = null;
  if (hasChildren) {
    sideMenuEl = React.createElement(DrawerSideMenu, null,
      React.createElement('div', { style: { height: 12 } }),
      item.children.map(function(c) {
        var isActive = pathname === c.path;
        var count = getChildCount(c);
        return React.createElement(SideMenuItem, {
          key: c.key,
          active: isActive,
          onClick: function() { history.push(c.path); }
        },
          c.icon && React.createElement(Icon, { type: c.icon, style: { fontSize: 14, flexShrink: 0 } }),
          React.createElement('span', { style: { flex: 1 } }, c.label),
          count !== null && React.createElement(Badge, {
            count: count,
            showZero: true,
            style: {
              backgroundColor: isActive ? '#1890ff' : '#f0f0f0',
              color: isActive ? '#fff' : '#8c8c8c',
              fontSize: 11,
              boxShadow: 'none',
              fontWeight: 500
            }
          })
        );
      })
    );
  }

  return React.createElement(Drawer, {
    visible: isOpen && !!content,
    onClose: handleClose,
    // A matriz de permissões (grupos nas colunas) precisa de mais largura
    width: pathname === '/configuracoes/permissoes'
      ? Math.min(Math.round(window.innerWidth * 0.95), 1560)
      : 1100,
    zIndex: 1010,
    destroyOnClose: true,
    keyboard: true,
    title: drawerTitle,
    headerStyle: { padding: '12px 24px' },
    bodyStyle: { padding: '20px 24px', background: '#fafafa', minHeight: '100vh' }
  },
    breadcrumbEl,
    hasChildren ? (
      React.createElement(DrawerLayout, null,
        sideMenuEl,
        React.createElement(DrawerContent, null, content)
      )
    ) : content
  );
};

export default SettingsDrawer;
 
